export type CodableDriftKind =
  | 'prod_ahead'
  | 'dev_behind'
  | 'prod_behind'
  | 'dev_dirty'
  | 'preview_waiting'
  | 'git_fetch'
  | null;

export interface CodableCarousel {
  live?: string | null;
  preview?: string | null;
  reserved?: string | null;
  preview_is_live?: boolean;
}

export interface CodableTopologyHint {
  split?: boolean;
  target?: string | null;
  actual?: string | null;
}

export type FeVersionSlot = 'dev' | 'preview' | 'reserved';
export type TopologyFeMode = 'beta' | 'simple' | 'normal' | 'full_split';

export function topologyFeMode(topology?: CodableTopologyHint | null): TopologyFeMode {
  const raw = (topology?.actual && topology.actual !== 'none'
    ? topology.actual
    : topology?.target) ?? '';
  if (raw === 'full_split' || raw === 'normal' || raw === 'simple' || raw === 'beta') {
    return raw;
  }
  return topology?.split ? 'normal' : 'beta';
}

/**
 * Kolik FE verzí ukázat: Normal/Full Split = DEV + náhled + záloha.
 * Simple = jeden, nebo dva když karusel drží dvě. Beta = jen DEV.
 */
export function feVersionSlots(
  topology?: CodableTopologyHint | null,
  carousel?: CodableCarousel | null,
): FeVersionSlot[] {
  const mode = topologyFeMode(topology);
  if (mode === 'normal' || mode === 'full_split') {
    return ['dev', 'preview', 'reserved'];
  }
  if (mode === 'simple') {
    const slots: FeVersionSlot[] = ['dev'];
    if (carousel?.preview && carousel.preview_is_live === false) {
      slots.push('preview');
    } else if (
      carousel?.reserved &&
      carousel.live &&
      carousel.reserved !== carousel.live
    ) {
      slots.push('reserved');
    }
    return slots;
  }
  return ['dev'];
}

/** DEV≠PROD u splitu je běžný stav, ne drift. Hlásí se až náhled ≠ ostrá. */
export function headlineDriftState(states: Iterable<string>): CodableDriftKind {
  const s = new Set(states);
  if (s.has('prod_ahead_git') || s.has('prod_diverged_git')) return 'prod_ahead';
  if (s.has('dev_behind_git')) return 'dev_behind';
  if (s.has('prod_behind_git')) return 'prod_behind';
  if (s.has('dev_dirty')) return 'dev_dirty';
  if (s.has('prod_preview_waiting')) return 'preview_waiting';
  if (s.has('git_fetch_warning')) return 'git_fetch';
  return null;
}

export function sameReleaseId(a?: string | null, b?: string | null): boolean {
  const aa = (a ?? '').trim().toLowerCase();
  const bb = (b ?? '').trim().toLowerCase();
  if (aa.length < 7 || bb.length < 7) return false;
  return aa === bb || aa.startsWith(bb) || bb.startsWith(aa);
}

/** Ostrá verze — live, nebo náhled když ukazuje totéž co ostrá. */
export function prodReleaseId(
  carousel?: CodableCarousel | null,
  prodHead?: string | null,
): string | null {
  const live = (carousel?.live ?? '').trim();
  if (live) return live;
  if (carousel?.preview_is_live && (carousel.preview ?? '').trim()) {
    return carousel.preview!.trim();
  }
  const head = (prodHead ?? '').trim();
  return head || null;
}

/** Dvě verze FE = náhled není ostrá, nebo záloha je jiná než ostrá. */
export function hasDistinctFeVersions(carousel?: CodableCarousel | null): boolean {
  if (!carousel) return false;
  if (carousel.preview && carousel.preview_is_live === false) return true;
  const ids = [carousel.live, carousel.preview, carousel.reserved]
    .map((x) => (x ?? '').trim())
    .filter(Boolean);
  return new Set(ids).size >= 2;
}
